// libs
import { useState } from 'react';
import { Grid } from '@mui/material';

// custom
import { mechanicViews } from '../layouts/MechanicViews';
import ButtonComponent from '../components/ButtonComponent';

//types
import type {
  GetComponentViewProps,
  AdditionalDetailsState,
  AdditionalDetailsFormProps,
  GenericButtonProps,
  GetFormButtonsProps,
} from '../types/AdditionalDetailsTypes';

function getFormView<T extends AdditionalDetailsState>(
  props: GetComponentViewProps<T>
): JSX.Element {
  // reuse the Collect & Get mechanicView for Auto CnG promo
  const formView =
    props.mechanic === 'Auto Collect & Get' ? 'Collect & Get' : props.mechanic;

  if (!(formView in mechanicViews))
    throw new Error(`Invalid mechanic: ${props.mechanic}`);

  return mechanicViews[formView](props);
}

function getFormButtons(
  props: GetFormButtonsProps
): GenericButtonProps<AdditionalDetailsFormProps['onSaveClickHandler']>[] {
  const {
    formIsDirty,
    handleDiscardChanges,
    pageErrors,
    onSaveClickHandler,
    formState,
  } = props;

  const enabledAndNoKeyValue = formState.additionalInfoEnabled && !formState.customAdditionalInfo.length
  const buttons: GenericButtonProps<AdditionalDetailsFormProps['onSaveClickHandler']>[] = [];

  if (formIsDirty) {
    buttons.push({
      styles: { width: '100px' },
      buttonText: 'Discard',
      testId: 'additional-details-discard-button',
      disabledCondition: false,
      onClickHandler: handleDiscardChanges,
      variant: 'outlined' as const,
      color: 'primary' as const,
    });
  }

  buttons.push({
    styles: { width: '100px' },
    buttonText: 'Save',
    testId: 'additional-details-save-button',
    disabledCondition: !!pageErrors.size || !formIsDirty || enabledAndNoKeyValue,
    onClickHandler: onSaveClickHandler,
    variant: 'contained' as const,
    color: 'primary' as const,
  });

  return buttons;
}

function FormView(props: AdditionalDetailsFormProps) {
  const {
    formIsDirty,
    handleDiscardChanges,
    pageErrors,
    onSaveClickHandler,
    updateStateValues,
    formState,
    ...otherProps
  } = props;

  const [modalOpen, setModalOpen] = useState(false);

  const formButtons = (
    <Grid
      container
      justifyContent='flex-end'
      spacing={2}
      sx={{ paddingRight: '9.5rem' }}
    >
      {getFormButtons({
        formIsDirty,
        handleDiscardChanges,
        pageErrors,
        onSaveClickHandler,
        formState,
      }).map((buttonProps) => (
        <ButtonComponent
          key={`additional-details-${buttonProps.buttonText}`}
          {...buttonProps}
        />
      ))}
    </Grid>
  );

  const formView = getFormView({
    ...otherProps,
    formState,
    pageErrors,
    modalOpen,
    setModalOpen,
    updateStateValues,
  });

  return (
    <>
      {formView}
      {formButtons}
    </>
  );
}

export function useAdditionalDetailsFormBuilder(
  props: AdditionalDetailsFormProps
) {
  return <FormView {...props} />;
}
